import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import axios from "axios";
import { toast } from "react-toastify";
import { adjustContestantVotes } from "../../../api/competitionFinalNight";

/**
 * Admin add/deduct contestant vote adjustments during pre-round (competition active).
 */
export default function CompetitionVoteAdjustmentPanel({
  competitionId,
  token,
  competitionStatus,
  finalNightStatus = "off",
}) {
  const [loading, setLoading] = useState(true);
  const [contestants, setContestants] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [amount, setAmount] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const finalNightActive =
    finalNightStatus === "live" || finalNightStatus === "ended";

  const canAdjust =
    competitionStatus === "running" ||
    competitionStatus === "paused" ||
    competitionStatus === "scheduled" ||
    finalNightActive;

  const load = useCallback(async () => {
    if (!token || !competitionId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/getCompetitionContestants/${competitionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.success) {
        setContestants(res.data.data || []);
      } else {
        toast.error(res.data?.message || "Failed to load contestants");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load contestants");
    } finally {
      setLoading(false);
    }
  }, [competitionId, token]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdjust = async (sign) => {
    if (!selectedId) {
      toast.info("Select a contestant first");
      return;
    }
    const delta = sign * Math.abs(Number(amount) || 1);
    if (!delta) return;
    setSubmitting(true);
    try {
      const res = await adjustContestantVotes(
        {
          competition_id: Number(competitionId),
          contestant_id: Number(selectedId),
          adjustment: delta,
          apply_to_active_round: finalNightActive,
        },
        token
      );
      if (res.data?.success) {
        toast.success(res.data.message || "Vote adjustment applied");
        const adj = res.data.data?.admin_vote_adjustment;
        if (adj != null) {
          setContestants((prev) =>
            prev.map((c) =>
              String(c.id) === String(selectedId)
                ? { ...c, admin_vote_adjustment: adj }
                : c
            )
          );
        } else {
          load();
        }
      } else {
        toast.error(res.data?.message || "Adjustment failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Adjustment failed");
    } finally {
      setSubmitting(false);
    }
  };

  const selected = contestants.find((c) => String(c.id) === String(selectedId));

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mb: 3,
        borderRadius: 3,
        border: "1px solid #eceef3",
        bgcolor: "#fff",
      }}
    >
      <Typography fontWeight={800} sx={{ mb: 0.5 }}>
        Admin vote adjustment
      </Typography>
      <Typography sx={{ color: "#6b7280", fontSize: 13, mb: 2 }}>
        Add or deduct votes for contestants during the competition period or
        during Final Night (applies to the active round when Final Night is live).
      </Typography>

      {!canAdjust ? (
        <Alert severity="info">
          Vote adjustment is available while the competition is scheduled, active,
          paused, or during Final Night.
        </Alert>
      ) : loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={28} sx={{ color: "#ef364e" }} />
        </Box>
      ) : contestants.length === 0 ? (
        <Alert severity="warning">No contestants registered yet.</Alert>
      ) : (
        <>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            gap={1.5}
            alignItems={{ sm: "center" }}
            flexWrap="wrap"
          >
            <TextField
              select
              label="Contestant"
              size="small"
              sx={{ minWidth: 280 }}
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="">
                <em>Select contestant</em>
              </MenuItem>
              {contestants.map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>
                  {c.full_name}
                  {c.category ? ` · ${c.category}` : ""}
                  {c.admin_vote_adjustment
                    ? ` (adj ${c.admin_vote_adjustment > 0 ? "+" : ""}${c.admin_vote_adjustment})`
                    : ""}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              type="number"
              size="small"
              label="Amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              sx={{ width: 100 }}
              inputProps={{ min: 1 }}
            />
            <Button
              size="small"
              variant="contained"
              color="success"
              disabled={submitting}
              startIcon={<AddIcon />}
              onClick={() => handleAdjust(1)}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Add votes
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              disabled={submitting}
              startIcon={<RemoveIcon />}
              onClick={() => handleAdjust(-1)}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Deduct votes
            </Button>
          </Stack>
          {selected ? (
            <Chip
              size="small"
              sx={{ mt: 1.5, fontWeight: 700 }}
              label={`Current admin adjustment: ${selected.admin_vote_adjustment ?? 0}`}
            />
          ) : null}
        </>
      )}
    </Paper>
  );
}
